import { FormProvider, useForm } from "react-hook-form";
import { classes } from "../../../../../util/style-utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { useContext } from "react";
import GeaoVerticalStepperLayout from "../../shared/Layout/GeaoVerticalStepperLayout";
import PrincipalFlowVerticalStepperElements from "../../../constants/PrincipalFlowVerticalStepperElements";
import useTitle from "../../../../../hooks/useTitle";
import { validationSchema } from "./validation";
import { GeaoContext } from "../../../GeaoContextProvider";
import { interviewToFormData } from "./mappers";
import useFormEvents from "./useFormEvents";
import Ssn from "../../../../gdk/Ssn";
import Fein from "../../../../gdk/Fein";
import AddressSection from "./AddressSection";
import SelectBox from "../../../../gds/SelectBox";
import { ExemptPayeeCodeOptions } from "./ExemptPayeeCodeOptions";
import { ExemptionFromFatcaReportingCodeOptions } from "./ExemptionFromFatcaReportingCodeOptions";
import { FederalTaxClassificationOptions } from "./FederalTaxClassificationOptions";
import { LlcTypeOptions } from "./LlcTypeOptions";

export default function PrincipalW9TaxForm() {
    useTitle("W-9 Tax Form Information");
    const { agencyInterview, agentInterview, interviewIsCompleted } = useContext(GeaoContext);

    const useFormReturn = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: interviewToFormData(agencyInterview, agentInterview)
    });

    const { register, handleSubmit, formState: { errors }, trigger, getValues, watch, control } = useFormReturn;

    const { onSubmit, onSaveAndExit, saving } = useFormEvents(getValues, trigger);

    const watchTaxClassification = watch("taxClassification");
    const watchSsn = watch("ssn");
    const watchFein = watch("fein");

    const ssnDisabled = watchFein && watchFein !== "";
    const feinDisabled = watchSsn && watchSsn !== "";

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="W-9 Tax Form"
            header="W-9 Tax Form Information"
        >
            <FormProvider {...useFormReturn}>
                <p>
                    To receive your commission payments, please fill out the form below.
                    You will have an opportunity to review the completed W-9 form once you reach the "Finalize" step in the application process.
                </p>
                <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                    <div className="flex-row label-row">
                        <div className="form-field">
                            <label htmlFor="individual-name" className="text">
                                Name of Entity/Individual{/*
                            */}<span className='required'>*</span>
                            </label>
                        </div>
                        <div className="form-field">
                            <label htmlFor="business-name" className="text">
                                Business Name or Disregarded Entity Name
                            </label>
                            <span className="form-descriptive-copy">Enter if Different Than Name of Entity/Individual</span>
                        </div>
                    </div>

                    <div className="flex-row">
                        <div className={classes("form-field", errors.individualName && "form-field--error")}>
                            <input id="individual-name" type="text" {...register("individualName")} />
                            {errors.individualName &&
                                <span className="form-message">{errors.individualName.message}</span>
                            }
                        </div>
                        <div className={classes("form-field", errors.businessName && "form-field--error")}>
                            <input id="business-name" type="text" {...register("businessName")} />
                            {errors.businessName &&
                                <span className="form-message">{errors.businessName.message}</span>
                            }
                        </div>
                    </div>

                    <div className="flex-row">
                        <SelectBox
                            control={control}
                            name="taxClassification"
                            label={<>
                                Federal Tax Classification{/*
                                */}<span className='required'>*</span>
                            </>}
                            options={FederalTaxClassificationOptions}
                            placeholder="Please Select"
                        />

                        {watchTaxClassification === "Other" &&
                            <div className={classes("form-field", errors.otherTaxClass && "form-field--error")}>
                                <label htmlFor="other-tax-class" className="text">
                                    Please Specify Tax Classification{/*
                                    */}<span className='required'>*</span>
                                </label>
                                <input id="other-tax-class" type="text" {...register("otherTaxClass")} />
                                {errors.otherTaxClass &&
                                    <span className="form-message">{errors.otherTaxClass.message}</span>
                                }
                            </div>
                        }

                        {watchTaxClassification === "Limited Liability Company (LLC)" &&
                            <SelectBox
                                control={control}
                                name="llcType"
                                label={<>
                                    LLC Type{/*
                                    */}<span className='required'>*</span>
                                </>}
                                options={LlcTypeOptions}
                                placeholder="Please Select"
                            />
                        }
                    </div>

                    <div className="flex-row">
                        <SelectBox
                            control={control}
                            name="exemptCode"
                            label="Exempt Payee Code"
                            description="Optional"
                            options={ExemptPayeeCodeOptions}
                            placeholder="Please Select"
                        />

                        <SelectBox
                            control={control}
                            name="fatcaCode"
                            label="Exemption From FATCA Reporting Code"
                            description="Optional"
                            options={ExemptionFromFatcaReportingCodeOptions}
                            placeholder="Please Select"
                        />
                    </div>

                    <AddressSection />

                    <div className="flex-row">
                        <div className={classes("form-field", errors.accountNumbers && "form-field--error")}>
                            <label htmlFor="account-numbers" className="text">
                                Account Numbers
                            </label>
                            <span className="form-descriptive-copy">Optional</span>
                            <input id="account-numbers" type="text" {...register("accountNumbers")} />
                            {errors.accountNumbers &&
                                <span className="form-message">{errors.accountNumbers.message}</span>
                            }
                        </div>
                    </div>

                    <h3 className="margin-top margin-bottom">Taxpayer Identification Number</h3>

                    <div className="flex-row">
                        <Ssn trackingId="PrincipalFlow:W9TaxForm:PrincipalW9TaxForm:TaxpayerSsn"
                            control={control}
                            name="ssn"
                            label={<>
                                SSN
                                {!ssnDisabled ? <span className='required'>*</span> : ""}
                            </>}
                            disabled={ssnDisabled}
                        />
                    </div>

                    <h4 className="margin-bottom">Or</h4>

                    <div className="flex-row">
                        <Fein trackingId="PrincipalFlow:W9TaxForm:PrincipalW9TaxForm:AgencyFein"
                            control={control}
                            name="fein"
                            label={<>
                                Agency Federal Employer Identification Number (FEIN)
                                {!feinDisabled ? <span className='required'>*</span> : ""}
                            </>}
                            disabled={feinDisabled}
                        />
                    </div>

                    {errors.taxId &&
                        <div className="flex-row">
                            <div className="form-field form-field--error">
                                <span className="form-message">{errors.taxId.message}</span>
                            </div>
                        </div>
                    }

                    <div className="flex-row">
                        <p>
                            <span className='required'>*</span> Indicates required field.
                        </p>
                    </div>

                    <div className="margin-top">
                        <button data-onboarding-button-id="PrincipalFlow:W9TaxForm:PrincipalW9TaxForm:Continue"
                            type="submit"
                            className="btn btn--primary btn--full-mobile btn--pull-right"
                            disabled={saving || interviewIsCompleted}
                        >
                            <span>Next</span>
                        </button>

                        <button data-onboarding-button-id="PrincipalFlow:W9TaxForm:PrincipalW9TaxForm:SaveAndExit"
                            type="button"
                            className="btn btn--secondary btn--full-mobile btn--pull-right"
                            onClick={onSaveAndExit}
                            disabled={saving || interviewIsCompleted}
                        >
                            <span>Save And Exit</span>
                        </button>
                    </div>
                </form>
            </FormProvider>
        </GeaoVerticalStepperLayout>
    )
};